import * as React from 'react';
import deepmerge from 'deepmerge';

import { importBPlayer } from './importer';
import {
  CombinedPlayerOptions,
  MediaPlayer,
  PlaylistMediaPlayer,
  VideoPlayerProps,
  VideoPlaylistPlayerProps,
} from './types';
import { generatePlayerOptions, generatePlaylistPlayerOptions } from './utils';

type ExternalProps = VideoPlayerProps | VideoPlaylistPlayerProps;

export function useBPlayer<T extends ExternalProps>(
  ref: React.MutableRefObject<HTMLElement | null>,
  props: T,
  isPlaylist = false
) {
  const { env = 'production', onReady, options: externalOptions } = props as T;
  const { importOptions = {} } = externalOptions;

  const [playerLoaded, setPlayerLoaded] = React.useState(false);
  const [player, setPlayer] = React.useState<
    MediaPlayer | PlaylistMediaPlayer | null
  >(null);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [options, setOptions] = React.useState<any>();

  //
  const [localPlaylist, setLocalPlaylist] = React.useState<
    CombinedPlayerOptions[]
  >([]);

  // dynamically import bplayer on initial pass
  // Nov 1, 2022 - This is now just a glorified
  // script append operation returning a Promise.
  // In order to improve stability w/ Webpack bundler,
  // which otherwise requires a /* webpackIgnore: true */ magic
  // string to make the dynamic import work
  // https://github.com/webpack/webpack/issues/8341
  React.useEffect(() => {
    importBPlayer(env, importOptions).then((bplayer) => {
      if (bplayer) {
        setPlayerLoaded(true);
      }
    });
  }, []);

  // Once player is loaded and we have
  // a referene to the DOM node, instantiate
  // BPlayer
  React.useEffect(() => {
    if (!(playerLoaded && ref.current)) {
      return;
    }

    let mediaPlayer: MediaPlayer | PlaylistMediaPlayer;

    if (isPlaylist) {
      const { itemOptions, playlistOption } =
        generatePlaylistPlayerOptions<T>(props);

      const combinedPlaylist = (
        (props.playlist as CombinedPlayerOptions[]) || []
      ).map((playlistItem) =>
        deepmerge.all<CombinedPlayerOptions>([itemOptions, playlistItem])
      );

      mediaPlayer = window.BPlayer.createPlaylistPlayer(
        ref.current,
        combinedPlaylist,
        playlistOption
      );

      // sets options in state
      setOptions(playlistOption);

      // set playlist in state
      setLocalPlaylist(combinedPlaylist);
    } else {
      const { options: playerOptions, callbacks } =
        generatePlayerOptions<T>(props);

      mediaPlayer = window.BPlayer.create(
        ref.current,
        playerOptions,
        callbacks
      );

      setOptions(playerOptions);
    }

    // set mediaplayer in state
    setPlayer(mediaPlayer);
  }, [playerLoaded, ref.current]);

  React.useEffect(() => {
    if (!player) {
      return undefined;
    }
    if (onReady) {
      player.ready(onReady);
    }

    return () => {
      player?.dispose();
    };
  }, [player]);

  return { loaded: playerLoaded, player, playlist: localPlaylist, options };
}
